import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL, formatDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { GiderForm } from "../components/GiderForm";
import { KalemCombobox } from "../components/KalemCombobox";
import { ButceRapor } from "../components/ButceRapor";
import { DisKaynakSekmesi } from "../components/DisKaynakSekmesi";
import { KalemEkleDialog } from "../components/KalemEkleDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Search,
  PlusCircle,
  ListFilter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronRight as ChevronRightSmall,
  Trash2,
  Wallet,
  TrendingDown,
  PiggyBank,
  Pencil,
  Table2,
  AlertTriangle,
  Lock,
  LockOpen,
  Briefcase,
} from "lucide-react";

const PAGE_SIZE = 50;
const HEPSI = "__hepsi__";
const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const AYLAR_KISA = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

// Kullanım % renk sınıfları: >95 kırmızı, >70 sarı
const kullanimClass = (pct) => {
  if (pct === null) return "text-muted-foreground";
  if (pct > 95) return "bg-red-50 text-red-700 border border-red-200";
  if (pct > 70) return "bg-amber-50 text-amber-800 border border-amber-200";
  return "bg-emerald-50 text-emerald-800 border border-emerald-200";
};

const hesaplaPct = (butce, gercek) => {
  if (butce > 0) return Math.round((gercek / butce) * 100);
  if (gercek > 0) return 999; // bütçesiz harcama
  return null;
};

const PctBadge = ({ butce, gercek }) => {
  const pct = hesaplaPct(butce, gercek);
  return (
    <span
      className={`inline-flex min-w-[52px] justify-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${kullanimClass(pct)}`}
    >
      {pct === null ? "—" : pct === 999 ? ">100%" : `%${pct}`}
    </span>
  );
};

export default function GiderPage() {
  const { user } = useAuth();
  const isYonetici = user.role === "yonetici";
  const canDelete = user.role === "yonetici" || user.role === "operasyon";
  const [tab, setTab] = useState("defter");
  const [kalemler, setKalemler] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api
      .get("/butce-kalemleri")
      .then((res) => setKalemler(res.data))
      .catch(() => toast.error("Bütçe kalemleri yüklenemedi."));
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="space-y-4"
    >
      <MaasUyariBandi canDelete={canDelete} onTemizlendi={() => setRefreshKey((k) => k + 1)} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-12 max-w-full justify-start overflow-x-auto bg-secondary">
          <TabsTrigger value="defter" className="h-10 gap-1.5 px-4" data-testid="gider-tab-defter">
            <ListFilter className="h-4 w-4" />
            Gider Defteri
          </TabsTrigger>
          <TabsTrigger value="butce" className="h-10 gap-1.5 px-4" data-testid="gider-tab-butce">
            <Table2 className="h-4 w-4" />
            Canlı Bütçe
          </TabsTrigger>
          <TabsTrigger value="rapor" className="h-10 gap-1.5 px-4" data-testid="gider-tab-rapor">
            <PiggyBank className="h-4 w-4" />
            Bütçe Raporu
          </TabsTrigger>
          <TabsTrigger value="diskaynak" className="h-10 gap-1.5 px-4" data-testid="gider-tab-diskaynak">
            <Briefcase className="h-4 w-4" />
            Dış Kaynak
          </TabsTrigger>
        </TabsList>

        <TabsContent value="defter" className="mt-4">
          <GiderDefteri kalemler={kalemler} refreshKey={refreshKey} />
        </TabsContent>

        <TabsContent value="butce" className="mt-4">
          <CanliButce kalemler={kalemler} isYonetici={isYonetici} aktif={tab === "butce"} />
        </TabsContent>

        <TabsContent value="rapor" className="mt-4">
          <ButceRapor yil={2026} />
        </TabsContent>

        <TabsContent value="diskaynak" className="mt-4">
          <DisKaynakSekmesi yil={2026} />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}

/* ================= MAAŞ GİDER UYARI BANDI ================= */

function MaasUyariBandi({ canDelete, onTemizlendi }) {
  const [veri, setVeri] = useState(null);
  const [listeOpen, setListeOpen] = useState(false);
  const [silOnay, setSilOnay] = useState(false);
  const [siliyor, setSiliyor] = useState(false);

  const fetchVeri = useCallback(() => {
    api
      .get("/gider/maas-kontrol")
      .then((res) => setVeri(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchVeri();
  }, [fetchVeri]);

  if (!veri || veri.adet === 0) return null;

  const temizle = async () => {
    setSiliyor(true);
    try {
      const res = await api.delete("/gider/maas-temizle");
      toast.success(res.data.message || "Maaş gider kayıtları silindi.");
      setSilOnay(false);
      setListeOpen(false);
      fetchVeri();
      onTemizlendi?.();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıtlar silinemedi."));
    } finally {
      setSiliyor(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50/70 px-4 py-3" data-testid="gider-maas-uyari-bandi">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-sm text-amber-900">
          <strong>{veri.adet}</strong> adet elle girilmiş maaş gider kaydı bulundu (toplam{" "}
          <strong className="tabular-nums">{formatTL(veri.toplam)}</strong>). Personel giderleri yalnızca Personel modülünden yönetilir.
        </p>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 border-amber-300 bg-white text-xs hover:bg-amber-100"
            onClick={() => setListeOpen(true)}
            data-testid="gider-maas-liste-btn"
          >
            Kayıtları Gör
          </Button>
          {canDelete && (
            <Button
              size="sm"
              className="h-8 bg-red-600 text-xs text-white hover:bg-red-700"
              onClick={() => setSilOnay(true)}
              data-testid="gider-maas-temizle-btn"
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              Tümünü Sil
            </Button>
          )}
        </div>
      </div>

      <Dialog open={listeOpen} onOpenChange={setListeOpen}>
        <DialogContent className="bg-card max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="gider-maas-liste-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Elle Girilmiş Maaş Gider Kayıtları</DialogTitle>
            <DialogDescription>
              Bu kayıtlar personel maaş kalemine elle girilmiş. Tek kaynak Personel modülü olmalıdır — temizlik önerilir.
            </DialogDescription>
          </DialogHeader>
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Tarih</th>
                  <th className="px-3 py-2">Kalem</th>
                  <th className="px-3 py-2">Tedarikçi</th>
                  <th className="px-3 py-2 text-right">Toplam</th>
                </tr>
              </thead>
              <tbody>
                {(veri.kayitlar || []).map((k) => (
                  <tr key={k.id} className="border-t border-border/60" data-testid="gider-maas-kayit-satiri">
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">{formatDate(k.tarih)}</td>
                    <td className="px-3 py-2">{k.kalem_adi}</td>
                    <td className="px-3 py-2">{k.tedarikci || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatTL(k.toplam)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={silOnay} onOpenChange={setSilOnay}>
        <AlertDialogContent data-testid="gider-maas-sil-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Maaş gider kayıtları silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {veri.adet} kayıt (toplam {formatTL(veri.toplam)}) kalıcı olarak silinecek. Personel giderleri Personel modülünden
              otomatik beslenmeye devam eder. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="gider-maas-sil-vazgec">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={temizle}
              disabled={siliyor}
              className="bg-red-600 hover:bg-red-700"
              data-testid="gider-maas-sil-onayla"
            >
              {siliyor ? "Siliniyor…" : "Tümünü Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* ================= SEKME 1: GİDER DEFTERİ ================= */

function GiderDefteri({ kalemler, refreshKey }) {
  const [formKey, setFormKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ baslangic: "", bitis: "", butce_kalemi_id: "", ana_kategori: "" });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const kategoriler = useMemo(
    () => Array.from(new Set(kalemler.map((k) => k.ana_kategori))),
    [kalemler]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
      Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
      if (debouncedSearch) params.q = debouncedSearch;
      const res = await api.get("/gider", { params });
      setData(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıtlar yüklenemedi."));
    } finally {
      setLoading(false);
    }
    // refreshKey bilinçli bağımlılıktır: üst bileşen artırdığında liste yeniden yüklenir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters, debouncedSearch, refreshKey]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      await api.post("/gider", payload);
      toast.success("Gider kaydı eklendi. Canlı bütçe güncellendi.");
      setFormKey((k) => k + 1);
      fetchList();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıt eklenemedi."));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload) => {
    setSaving(true);
    try {
      await api.put(`/gider/${editTarget.id}`, payload);
      toast.success("Kayıt güncellendi.");
      setEditTarget(null);
      fetchList();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıt güncellenemedi."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/gider/${deleteTarget.id}`);
      toast.success("Kayıt silindi.");
      setDeleteTarget(null);
      setEditTarget(null);
      fetchList();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıt silinemedi."));
    }
  };

  const kayitlar = data?.kayitlar || [];
  const totalPages = Math.max(1, Math.ceil((data?.toplam_kayit || 0) / PAGE_SIZE));

  return (
    <div className="space-y-4">
      {/* Giriş formu */}
      <div className="rounded-xl border bg-card p-4 sm:p-6 shadow-[0_1px_0_rgba(14,27,46,0.06),0_10px_30px_rgba(14,27,46,0.06)] w-full">
        <h2 className="font-display text-lg font-semibold mb-4">Gider Girişi</h2>
        <GiderForm key={formKey} kalemler={kalemler} onSubmit={handleCreate} submitting={saving} />
      </div>

      {/* Filtreler */}
      <div className="rounded-xl border bg-card p-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Başlangıç</Label>
            <Input type="date" value={filters.baslangic} onChange={(e) => setFilter("baslangic", e.target.value)} className="h-10" data-testid="gider-filter-baslangic" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Bitiş</Label>
            <Input type="date" value={filters.bitis} onChange={(e) => setFilter("bitis", e.target.value)} className="h-10" data-testid="gider-filter-bitis" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Kategori</Label>
            <Select value={filters.ana_kategori || HEPSI} onValueChange={(v) => setFilter("ana_kategori", v === HEPSI ? "" : v)}>
              <SelectTrigger className="h-10" data-testid="gider-filter-kategori">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card">
                <SelectItem value={HEPSI}>Tümü</SelectItem>
                {kategoriler.map((k) => (
                  <SelectItem key={k} value={k}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Kalem</Label>
            <div className="[&>button]:h-10">
              <KalemCombobox
                kalemler={kalemler}
                value={filters.butce_kalemi_id}
                onChange={(v) => setFilter("butce_kalemi_id", v)}
                placeholder="Tüm kalemler"
                testid="gider-filter-kalem"
              />
            </div>
          </div>
          <div className="space-y-1 col-span-2 sm:col-span-1">
            <Label className="text-xs text-muted-foreground">Ara</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Tedarikçi, belge, açıklama…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-10 pl-8" data-testid="gider-filter-search" />
            </div>
          </div>
        </div>
      </div>

      {/* Liste */}
      {loading && !data ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <>
          <div className="hidden md:block rounded-xl border bg-card overflow-hidden">
            <div className="w-full overflow-x-auto">
              <table className="w-full text-sm" data-testid="gider-table">
                <thead className="bg-muted/50">
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-3">Tarih</th>
                    <th className="px-3 py-3">Gider Kalemi</th>
                    <th className="px-3 py-3">Tedarikçi</th>
                    <th className="px-3 py-3">Ödeme</th>
                    <th className="px-3 py-3 text-right">Matrah</th>
                    <th className="px-3 py-3 text-right">KDV</th>
                    <th className="px-3 py-3 text-right">Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {kayitlar.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-10 text-center text-muted-foreground" data-testid="gider-empty-message">
                        Filtrelere uyan kayıt bulunamadı.
                      </td>
                    </tr>
                  ) : (
                    kayitlar.map((k) => (
                      <tr key={k.id} className="border-t border-border/60 hover:bg-[hsl(var(--primary))]/[0.03] cursor-pointer transition-colors" onClick={() => setEditTarget(k)} data-testid="gider-table-row">
                        <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">{formatDate(k.tarih)}</td>
                        <td className="px-3 py-2.5 max-w-[260px]">
                          <p className="truncate font-medium">{k.kalem_adi}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{k.ana_kategori}</p>
                        </td>
                        <td className="px-3 py-2.5 max-w-[160px] truncate">{k.tedarikci || "—"}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap"><Badge variant="secondary">{k.odeme_sekli}</Badge></td>
                        <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(k.matrah, 2)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(k.kdv, 2)}</td>
                        <td className="px-3 py-2.5 text-right font-medium tabular-nums whitespace-nowrap">{formatTL(k.toplam, 2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobil kartlar */}
          <div className="md:hidden space-y-2" data-testid="gider-mobile-list">
            {kayitlar.length === 0 ? (
              <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
                Filtrelere uyan kayıt bulunamadı.
              </div>
            ) : (
              kayitlar.map((k) => (
                <div key={k.id} className="rounded-xl border bg-card p-3.5" onClick={() => setEditTarget(k)} role="button" data-testid="gider-mobile-card">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground tabular-nums">{formatDate(k.tarih)}</span>
                    <Badge variant="secondary" className="text-[10px]">{k.odeme_sekli}</Badge>
                    <span className="ml-auto font-display text-sm font-bold tabular-nums">{formatTL(k.toplam, 2)}</span>
                  </div>
                  <p className="mt-1 truncate text-sm font-medium">{k.kalem_adi}</p>
                  <p className="text-xs text-muted-foreground truncate">{k.tedarikci || k.ana_kategori}</p>
                </div>
              ))
            )}
          </div>

          {data && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border bg-card px-4 py-3 text-sm" data-testid="gider-totals-bar">
              <span className="text-muted-foreground"><strong className="text-foreground tabular-nums">{data.toplamlar.adet.toLocaleString("tr-TR")}</strong> kayıt</span>
              <span className="text-muted-foreground">Matrah: <strong className="text-foreground tabular-nums" data-testid="gider-total-matrah">{formatTL(data.toplamlar.matrah, 2)}</strong></span>
              <span className="text-muted-foreground">KDV: <strong className="text-foreground tabular-nums">{formatTL(data.toplamlar.kdv, 2)}</strong></span>
              <span className="text-muted-foreground">Toplam: <strong className="text-foreground tabular-nums">{formatTL(data.toplamlar.toplam, 2)}</strong></span>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="min-h-[44px] gap-1" data-testid="gider-pagination-prev">
                <ChevronLeft className="h-4 w-4" />Önceki
              </Button>
              <span className="text-xs text-muted-foreground tabular-nums">Sayfa {page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)} className="min-h-[44px] gap-1" data-testid="gider-pagination-next">
                Sonraki<ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Düzenleme */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-card max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Gider Kaydını Düzenle</DialogTitle>
            <DialogDescription>
              {editTarget && `${formatDate(editTarget.tarih)} • ${formatTL(editTarget.toplam, 2)}`}
            </DialogDescription>
          </DialogHeader>
          {editTarget && (
            <GiderForm key={editTarget.id} kalemler={kalemler} initial={editTarget} onSubmit={handleUpdate} submitting={saving} submitLabel="Güncelle" />
          )}
          <div className="flex justify-end">
            <Button variant="ghost" onClick={() => setDeleteTarget(editTarget)} className="min-h-[44px] gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid="gider-edit-delete-button">
              <Trash2 className="h-4 w-4" />Kaydı Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Kaydı Sil</AlertDialogTitle>
            <AlertDialogDescription>Bu gider kaydı kalıcı olarak silinecek. Canlı bütçe de güncellenecek. Devam edilsin mi?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-[44px]">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="min-h-[44px] bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="gider-delete-confirm-button">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ================= SEKME 2: CANLI BÜTÇE ================= */

function CanliButce({ kalemler, isYonetici, aktif }) {
  const [rapor, setRapor] = useState(null);
  const [loading, setLoading] = useState(true);
  const suAnkiAy = new Date().getMonth() + 1; // 1-12
  const [ay, setAy] = useState(String(suAnkiAy)); // "1".."12" veya "yil"
  const [acik, setAcik] = useState(() => new Set());
  const [editKalem, setEditKalem] = useState(null);
  const [yeniKalemOpen, setYeniKalemOpen] = useState(false);
  const [editButce, setEditButce] = useState([]);
  const [editGecmis, setEditGecmis] = useState([]);
  const [editKod, setEditKod] = useState("");
  const [saving, setSaving] = useState(false);
  const [ayIslem, setAyIslem] = useState(false);

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/butce/rapor", { params: { yil: 2026 } });
      setRapor(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Bütçe raporu yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (aktif) fetchRapor();
  }, [aktif, fetchRapor]);

  const grouped = useMemo(() => {
    if (!rapor) return [];
    const map = new Map();
    rapor.kalemler.forEach((k) => {
      if (!map.has(k.ana_kategori)) map.set(k.ana_kategori, []);
      map.get(k.ana_kategori).push(k);
    });
    return Array.from(map.entries());
  }, [rapor]);

  const ayIdx = ay === "yil" ? null : parseInt(ay) - 1;
  const kapaliAylar = useMemo(() => new Set(rapor?.kapali_aylar || []), [rapor]);
  const ayKapali = ayIdx !== null && kapaliAylar.has(ayIdx + 1);

  const handleAyToggle = async () => {
    if (ayIdx === null) return;
    setAyIslem(true);
    try {
      const res = await api.post(ayKapali ? "/butce/ay-ac" : "/butce/ay-kapat", { yil: 2026, ay: ayIdx + 1 });
      toast.success(res.data.message);
      fetchRapor();
    } catch (err) {
      toast.error(getErrMsg(err, "Ay kapama işlemi yapılamadı."));
    } finally {
      setAyIslem(false);
    }
  };

  const kalemDegerleri = useCallback(
    (k) => {
      if (ayIdx === null) {
        const butce = k.aylik_butce.reduce((a, b) => a + b, 0);
        const gercek = k.gerceklesen.reduce((a, b) => a + b, 0);
        return { butce, gercek, kalan: butce - gercek };
      }
      const butce = k.aylik_butce[ayIdx] || 0;
      const gercek = k.gerceklesen[ayIdx] || 0;
      return { butce, gercek, kalan: butce - gercek };
    },
    [ayIdx]
  );

  const ozet = useMemo(() => {
    if (!rapor) return { butce: 0, gercek: 0, kalan: 0 };
    let butce = 0, gercek = 0;
    rapor.kalemler.forEach((k) => {
      const d = kalemDegerleri(k);
      butce += d.butce;
      gercek += d.gercek;
    });
    return { butce, gercek, kalan: butce - gercek };
  }, [rapor, kalemDegerleri]);

  const toggleKategori = (kat) => {
    setAcik((s) => {
      const next = new Set(s);
      next.has(kat) ? next.delete(kat) : next.add(kat);
      return next;
    });
  };

  const tumunuToggle = () => {
    setAcik((s) => (s.size === grouped.length ? new Set() : new Set(grouped.map(([kat]) => kat))));
  };

  const openEdit = (k) => {
    setEditKalem(k);
    setEditKod(k.hesap_kodu || "");
    setEditButce(k.aylik_butce.map((v) => String(v)));
    setEditGecmis((k.gecmis_fiili || Array(12).fill(null)).slice(0, 6).map((v) => (v === null || v === undefined ? "" : String(v))));
  };

  const handleButceKaydet = async () => {
    const values = editButce.map((v) => parseFloat(v) || 0);
    if (values.some((v) => v < 0)) return toast.error("Bütçe tutarları negatif olamaz.");
    const gecmisValues = editGecmis.map((v) => (v === "" ? null : parseFloat(v)));
    if (gecmisValues.some((v) => v !== null && (isNaN(v) || v < 0))) return toast.error("Gerçekleşen tutarlar negatif olamaz.");
    setSaving(true);
    try {
      const yeniKod = editKod.trim();
      if (yeniKod !== (editKalem.hesap_kodu || "")) {
        await api.patch(`/butce-kalemleri/${editKalem.id}/bilgi`, { hesap_kodu: yeniKod });
      }
      await api.patch(`/butce-kalemleri/${editKalem.id}`, { aylik_butce: values });
      const tamGecmis = [...gecmisValues, ...((editKalem.gecmis_fiili || Array(12).fill(null)).slice(6))];
      await api.patch(`/butce-kalemleri/${editKalem.id}/gecmis-fiili`, { gecmis_fiili: tamGecmis });
      toast.success("Bütçe planı ve gerçekleşen değerler güncellendi.");
      setEditKalem(null);
      fetchRapor();
    } catch (err) {
      toast.error(getErrMsg(err, "Bütçe güncellenemedi."));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !rapor) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (!rapor) return null;

  return (
    <div className="space-y-4">
      {/* Ay seçici + özet kartlar */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={ay} onValueChange={setAy}>
          <SelectTrigger className="h-11 w-44" data-testid="butce-ay-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card">
            {AYLAR.map((a, i) => (
              <SelectItem key={a} value={String(i + 1)}>
                {a} 2026 {i + 1 < (rapor.canli_baslangic_ayi || 1) ? "• tarihsel" : "• canlı"}
              </SelectItem>
            ))}
            <SelectItem value="yil">Tüm Yıl (2026)</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={tumunuToggle} className="min-h-[44px]" data-testid="butce-toggle-all-button">
          {acik.size === grouped.length ? "Tümünü Kapat" : "Tümünü Aç"}
        </Button>
        {isYonetici && (
          <Button size="sm" onClick={() => setYeniKalemOpen(true)} className="min-h-[44px] gap-1.5" data-testid="butce-yeni-kalem-button">
            <PlusCircle className="h-4 w-4" /> Yeni Kalem
          </Button>
        )}
        {ayIdx !== null && (
          <div className="ml-auto flex items-center gap-2">
            <Badge
              className={`border text-[11px] font-semibold ${ayKapali ? "border-slate-700 bg-slate-800 text-white hover:bg-slate-800" : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50"}`}
              data-testid="butce-ay-durumu-rozet"
            >
              {ayKapali ? "Kapandı" : "Açık"}
            </Badge>
            {isYonetici && (
              <Button
                variant={ayKapali ? "outline" : "default"}
                size="sm"
                onClick={handleAyToggle}
                disabled={ayIslem}
                className="min-h-[44px] gap-1.5"
                data-testid="butce-ay-kapat-button"
              >
                {ayKapali ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                {ayIslem ? "İşleniyor…" : ayKapali ? "Kapamayı Geri Al" : "Ayı Kapat"}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="butce-ozet-kartlar">
        {[
          { label: "Toplam Planlanan", value: ozet.butce, icon: Wallet, cls: "" },
          { label: "Toplam Gerçekleşen", value: ozet.gercek, icon: TrendingDown, cls: "" },
          { label: "Toplam Kalan", value: ozet.kalan, icon: PiggyBank, cls: ozet.kalan < 0 ? "text-red-700" : "text-emerald-700" },
        ].map((c) => (
          <div key={c.label} className="relative overflow-hidden rounded-xl border bg-card p-4 shadow-[0_1px_0_rgba(14,27,46,0.06),0_8px_24px_rgba(14,27,46,0.06)]">
            <div className="absolute inset-x-0 top-0 h-0.5 bg-[hsl(var(--accent))]/40" />
            <div className="flex items-center gap-2 text-muted-foreground">
              <c.icon className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wide">{c.label}</span>
            </div>
            <p className={`mt-2 font-display text-xl md:text-2xl font-bold tabular-nums ${c.cls}`}>{formatTL(c.value)}</p>
          </div>
        ))}
      </div>

      {/* Tablo */}
      {ayIdx !== null ? (
        <AylikTablo grouped={grouped} acik={acik} toggleKategori={toggleKategori} kalemDegerleri={kalemDegerleri} isYonetici={isYonetici} openEdit={openEdit} />
      ) : (
        <YillikTablo grouped={grouped} acik={acik} toggleKategori={toggleKategori} isYonetici={isYonetici} openEdit={openEdit} />
      )}

      <p className="text-xs text-muted-foreground">
        Ocak–Haziran 2026 değerleri sabit tarihsel verilerdir. Temmuz 2026 ve sonrası gider kayıtlarından (matrah, KDV hariç) canlı hesaplanır. Personel kaleminin Gerçekleşen'i Personel modülünden otomatik gelir; Planlanan'ı elle düzenlenir. Kapatılan ayda dashboard'daki resmi Toplam Gider Gerçekleşen'den, açık ayda Planlanan'dan okunur.
      </p>

      {/* Bütçe planı düzenleme (yonetici) */}
      <KalemEkleDialog open={yeniKalemOpen} onClose={() => setYeniKalemOpen(false)} onSaved={fetchRapor} />

      <Dialog open={!!editKalem} onOpenChange={(o) => !o && setEditKalem(null)}>
        <DialogContent className="bg-card max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Bütçe Planını Düzenle</DialogTitle>
            <DialogDescription>
              {editKalem && `${editKalem.hesap_kodu} • ${editKalem.kalem_adi}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label className="text-xs">Gider Kalem No (Hesap Kodu)</Label>
            <Input
              value={editKod}
              onChange={(e) => setEditKod(e.target.value)}
              placeholder="örn. 770.02.01"
              className="h-10 tabular-nums"
              data-testid="butce-edit-hesap-kodu-input"
            />
            <p className="text-[11px] text-muted-foreground">Kod düzenlenebilir — başka bir kalemde kullanılan kod kabul edilmez.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {AYLAR.map((a, i) => (
              <div key={a} className="space-y-1">
                <Label className="text-xs">{a}</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={editButce[i] ?? ""}
                  onChange={(e) =>
                    setEditButce((arr) => {
                      const next = [...arr];
                      next[i] = e.target.value;
                      return next;
                    })
                  }
                  className="h-10 text-right tabular-nums"
                  data-testid={`butce-edit-ay-${i + 1}-input`}
                />
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Gerçekleşen Giderler (Ocak – Haziran)</p>
            <p className="text-[11px] text-muted-foreground">Geçmiş ayların fiili tutarları — düzenlediğinizde dashboard ve raporlara anında yansır.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {AYLAR.slice(0, 6).map((a, i) => (
                <div key={`g-${a}`} className="space-y-1">
                  <Label className="text-xs">{a} (fiili)</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    placeholder="—"
                    value={editGecmis[i] ?? ""}
                    onChange={(e) =>
                      setEditGecmis((arr) => {
                        const next = [...arr];
                        next[i] = e.target.value;
                        return next;
                      })
                    }
                    className="h-10 text-right tabular-nums"
                    data-testid={`gecmis-fiili-ay-${i + 1}-input`}
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditKalem(null)} className="min-h-[44px]">Vazgeç</Button>
            <Button onClick={handleButceKaydet} disabled={saving} className="min-h-[44px]" data-testid="butce-edit-submit-button">
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---- Aylık görünüm ---- */
function AylikTablo({ grouped, acik, toggleKategori, kalemDegerleri, isYonetici, openEdit }) {
  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="w-full overflow-x-auto">
        <table className="w-full text-sm" data-testid="butce-aylik-tablo">
          <thead className="bg-[hsl(var(--primary))] text-white">
            <tr className="text-left text-xs uppercase tracking-wide">
              <th className="px-3 py-3 min-w-[220px]">Kalem</th>
              <th className="px-3 py-3 text-right">Planlanan</th>
              <th className="px-3 py-3 text-right">Gerçekleşen</th>
              <th className="px-3 py-3 text-right">Kalan</th>
              <th className="px-3 py-3 text-center">Kullanım %</th>
              {isYonetici && <th className="px-3 py-3 w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {grouped.map(([kategori, items]) => {
              const kb = items.reduce((a, k) => a + kalemDegerleri(k).butce, 0);
              const kg = items.reduce((a, k) => a + kalemDegerleri(k).gercek, 0);
              const isOpen = acik.has(kategori);
              return (
                <FragmentSatir key={kategori}>
                  <tr
                    className="border-t bg-muted/60 cursor-pointer hover:bg-muted transition-colors"
                    onClick={() => toggleKategori(kategori)}
                    data-testid="butce-kategori-satiri"
                  >
                    <td className="px-3 py-2.5 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRightSmall className="h-4 w-4 text-muted-foreground" />}
                        <span className="font-display text-[13px]">{kategori}</span>
                        <Badge variant="secondary" className="text-[10px] tabular-nums">{items.length}</Badge>
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap">{formatTL(kb)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap">{formatTL(kg)}</td>
                    <td className={`px-3 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap ${kb - kg < 0 ? "text-red-700" : ""}`}>{formatTL(kb - kg)}</td>
                    <td className="px-3 py-2.5 text-center"><PctBadge butce={kb} gercek={kg} /></td>
                    {isYonetici && <td></td>}
                  </tr>
                  {isOpen &&
                    items.map((k) => {
                      const d = kalemDegerleri(k);
                      return (
                        <tr key={k.id} className="border-t border-border/50" data-testid="butce-kalem-satiri">
                          <td className="px-3 py-2 pl-9">
                            <p className="text-sm">{k.kalem_adi}</p>
                            <p className="text-[10px] text-muted-foreground tabular-nums">
                              {k.hesap_kodu}
                              {k.alt_grup && <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 font-medium" data-testid="butce-kalem-alt-grup">{k.alt_grup}</span>}
                            </p>
                            {k.kaynak_modul === "personel" && (
                              <p className="text-[10px] font-medium text-sky-700" data-testid="butce-personel-kaynak-etiketi">
                                Planlanan (elle) • Gerçekleşen (personelden otomatik)
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatTL(d.butce)}</td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatTL(d.gercek)}</td>
                          <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${d.kalan < 0 ? "text-red-700 font-medium" : ""}`}>{formatTL(d.kalan)}</td>
                          <td className="px-3 py-2 text-center"><PctBadge butce={d.butce} gercek={d.gercek} /></td>
                          {isYonetici && (
                            <td className="px-2 py-2 text-right">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(k)} data-testid="butce-kalem-duzenle-button" aria-label="Bütçeyi düzenle">
                                <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                </FragmentSatir>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---- Tüm Yıl kompakt görünüm ---- */
function YillikTablo({ grouped, acik, toggleKategori, isYonetici, openEdit }) {
  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="w-full overflow-x-auto">
        <table className="text-xs min-w-[1100px] w-full" data-testid="butce-yillik-tablo">
          <thead className="bg-[hsl(var(--primary))] text-white">
            <tr className="text-left uppercase tracking-wide">
              <th className="px-3 py-3 min-w-[200px] sticky left-0 bg-[hsl(var(--primary))]">Kalem</th>
              {AYLAR_KISA.map((a) => (
                <th key={a} className="px-2 py-3 text-right">{a}</th>
              ))}
              <th className="px-3 py-3 text-right bg-[hsl(var(--accent))]/20">Kümülatif</th>
            </tr>
          </thead>
          <tbody>
            {grouped.map(([kategori, items]) => {
              const isOpen = acik.has(kategori);
              const aySum = (m) => items.reduce((a, k) => a + (k.gerceklesen[m] || 0), 0);
              const ayButce = (m) => items.reduce((a, k) => a + (k.aylik_butce[m] || 0), 0);
              const kum = items.reduce((a, k) => a + k.gerceklesen.reduce((x, y) => x + y, 0), 0);
              return (
                <FragmentSatir key={kategori}>
                  <tr className="border-t bg-muted/60 cursor-pointer hover:bg-muted transition-colors" onClick={() => toggleKategori(kategori)} data-testid="butce-yillik-kategori-satiri">
                    <td className="px-3 py-2 font-medium sticky left-0 bg-muted/90 backdrop-blur">
                      <span className="inline-flex items-center gap-1">
                        {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRightSmall className="h-3.5 w-3.5 text-muted-foreground" />}
                        <span className="font-display">{kategori}</span>
                      </span>
                    </td>
                    {AYLAR_KISA.map((_, m) => {
                      const g = aySum(m);
                      const b = ayButce(m);
                      const pct = hesaplaPct(b, g);
                      return (
                        <td key={m} className={`px-2 py-2 text-right tabular-nums whitespace-nowrap font-medium ${pct !== null && pct > 95 ? "text-red-700" : pct !== null && pct > 70 ? "text-amber-700" : ""}`}>
                          {g === 0 ? "—" : formatTL(g)}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap bg-[hsl(var(--accent))]/10">{formatTL(kum)}</td>
                  </tr>
                  {isOpen &&
                    items.map((k) => {
                      const kumK = k.gerceklesen.reduce((a, b) => a + b, 0);
                      return (
                        <tr key={k.id} className="border-t border-border/50" data-testid="butce-yillik-kalem-satiri">
                          <td className="px-3 py-1.5 pl-8 sticky left-0 bg-card">
                            <span className="inline-flex items-center gap-1.5">
                              <span className="truncate max-w-[180px]" title={k.kalem_adi}>{k.kalem_adi}</span>
                              {isYonetici && (
                                <button onClick={() => openEdit(k)} className="text-muted-foreground hover:text-foreground" data-testid="butce-yillik-duzenle-button" aria-label="Düzenle">
                                  <Pencil className="h-3 w-3" />
                                </button>
                              )}
                            </span>
                          </td>
                          {AYLAR_KISA.map((_, m) => {
                            const g = k.gerceklesen[m] || 0;
                            const b = k.aylik_butce[m] || 0;
                            const pct = hesaplaPct(b, g);
                            return (
                              <td key={m} className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap ${pct !== null && pct > 95 ? "text-red-700" : pct !== null && pct > 70 ? "text-amber-700" : "text-foreground/80"}`} title={`Bütçe: ${formatTL(b)}`}>
                                {g === 0 ? "—" : formatTL(g)}
                              </td>
                            );
                          })}
                          <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap bg-[hsl(var(--accent))]/5 font-medium">{formatTL(kumK)}</td>
                        </tr>
                      );
                    })}
                </FragmentSatir>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// React.Fragment yerine tbody içinde çoklu satır döndürmek için yardımcı
function FragmentSatir({ children }) {
  return <>{children}</>;
}
